import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import CollectionCard from "./CollectionCard";
import ProjectCard from "./ProjectCard";
import "../styles/CardList.css";
import { useNavigate, useLocation } from "react-router-dom";

const CardList = ({ collections = [], Directiontype = "horizontal", type, projectCount, collectionInfo }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRestoredRef = useRef(false);
  
  // Restore visibleCollections from sessionStorage if available
  const getStoredVisibleCount = () => {
    if (type === "collection") {
      const stored = sessionStorage.getItem('explore-visibleCollections');
      return stored ? parseInt(stored, 10) : 20;
    } else {
      const stored = sessionStorage.getItem('project-visibleProjects');
      return stored ? parseInt(stored, 10) : 20;
    }
  };

  const [visibleCollections, setVisibleCollections] = useState(getStoredVisibleCount());
  const [visibleProjects, setVisibleProjects] = useState(getStoredVisibleCount());

  const handleScroll = () => {
    const windowHeight = "innerHeight" in window ? window.innerHeight : document.documentElement.offsetHeight;
    const body = document.body;
    const html = document.documentElement;
    const docHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
    const windowBottom = windowHeight + window.pageYOffset;

    if (windowBottom >= docHeight - 100) {
      if (type === "collection" && visibleCollections < collections.length) {
        const newCount = visibleCollections + 20;
        setVisibleCollections(newCount);
        // Save to sessionStorage
        sessionStorage.setItem('explore-visibleCollections', newCount.toString());
      } else if (type === "project" && visibleProjects < projectCount) {
        const newCount = visibleProjects + 20;
        setVisibleProjects(newCount);
        sessionStorage.setItem('project-visibleProjects', newCount.toString());
      }
    }
  };

  // Save scroll position and visible count when navigating to detail
  const handleCardClick = (collection) => {
    // Save current scroll position
    sessionStorage.setItem('explore-scrollPosition', window.pageYOffset.toString());
    // Save visible collections count
    if (type === "collection") {
      sessionStorage.setItem('explore-visibleCollections', visibleCollections.toString());
    }
    // Navigate with project ID in URL for shareable links
    const projectId = collection.id || collection.projectId;
    navigate(`/detail/${projectId}`, { state: { item: collection } });
  };

  // Restore scroll position after items are rendered
  useEffect(() => {
    if (type === "collection" && collections.length > 0 && !scrollRestoredRef.current) {
      const storedScroll = sessionStorage.getItem('explore-scrollPosition');
      if (storedScroll) {
        // Wait for DOM to update, then restore scroll
        setTimeout(() => {
          window.scrollTo(0, parseInt(storedScroll, 10));
          scrollRestoredRef.current = true;
          // Clear the stored scroll position after restoring
          sessionStorage.removeItem('explore-scrollPosition');
        }, 100);
      } else {
        // No stored scroll position, reset to top
        scrollRestoredRef.current = true;
      }
    }
  }, [collections.length, type]);

  // Reset scroll restoration flag and clear stored values when collections change significantly
  // (This happens when filters change, not when coming back from detail)
  useEffect(() => {
    if (type === "collection") {
      // If collections change but we don't have a stored scroll, it means filters changed
      // Clear any stored values to start fresh
      const hasStoredScroll = sessionStorage.getItem('explore-scrollPosition');
      if (!hasStoredScroll && scrollRestoredRef.current) {
        // Filters changed, reset visibleCollections to default
        setVisibleCollections(20);
        sessionStorage.removeItem('explore-visibleCollections');
      }
      scrollRestoredRef.current = false;
    }
  }, [collections.length, type]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [type === "collection" ? collections.length : projectCount, visibleProjects, visibleCollections]);

  return (
    <div 
      id="card-list" 
      style={{ flexDirection: Directiontype === "horizontal" ? "row" : "column" }}
      role="list"
      aria-label={type === "collection" ? "Collection list" : "Project list"}
    >
      {type === "collection" && collections.slice(0, visibleCollections).map((collection) => {
        // DEBUG: Log V3 projects being rendered
        const v3Contract = '0x99a9b7c1116f9ceeb1652de04d5969cce509b069';
        const isV3 = collection.contractAddress?.toLowerCase() === v3Contract.toLowerCase();
        if (isV3 && visibleCollections <= 40) { // Only log first few renders to avoid spam
          console.log(`[CardList] Rendering V3 project: ${collection.projectId} - ${collection.name}`);
        }
        
        return (
          <div key={collection.id || collection.projectId} role="listitem">
            <CollectionCard
              collectionInfo={collection}
              onClick={() => handleCardClick(collection)}
            />
          </div>
        );
      })}
      {type === "project" && [...Array(projectCount < 20 ? projectCount : visibleProjects)].map((v, index) => (
        <div key={`project-${collectionInfo?.projectId}-${index}`} role="listitem">
          <ProjectCard
            collectionInfo={collectionInfo}
            projectIndex={index}
            onClick={() => navigate("/projectdetail", { state: { collectionInfo: collectionInfo, projectIndex: index } })}
          />
        </div>
      ))}
    </div>
  );
};

CardList.propTypes = {
  collections: PropTypes.array,
  Directiontype: PropTypes.oneOf(["horizontal", "vertical"]),
  type: PropTypes.oneOf(["collection", "project"]).isRequired,
  projectCount: PropTypes.number,
  collectionInfo: PropTypes.object,
};

export default CardList;
